<template>
  <div v-if="hasData">

    <div class="cm-chart-title">
      <span>{{ dowTitle }}</span>
      <div class="cm-seg">
        <button
          :class="{ active: mode === 'activity' }"
          @click="setMode('activity')"
        >{{ $t('CM_DRILL_RHYTHM_MODE_ACTIVITY') }}</button>
        <button
          :class="{ active: mode === 'frequency' }"
          @click="setMode('frequency')"
        >{{ $t('CM_DRILL_RHYTHM_MODE_FREQUENCY') }}</button>
      </div>
    </div>
    <div class="m-chart">
      <div ref="dow" class="chart"></div>
    </div>

    <div class="cm-chart-title">
      <span>{{ hourTitle }}</span>
      <div class="cm-seg">
        <button
          :class="{ active: mode === 'activity' }"
          @click="setMode('activity')"
        >{{ $t('CM_DRILL_RHYTHM_MODE_ACTIVITY') }}</button>
        <button
          :class="{ active: mode === 'frequency' }"
          @click="setMode('frequency')"
        >{{ $t('CM_DRILL_RHYTHM_MODE_FREQUENCY') }}</button>
      </div>
    </div>
    <div class="m-chart">
      <div ref="hour" class="chart"></div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { debounce, merge } from "lodash";
import moment from "moment";
import comOpt from "@/models/charts/common-options.json";
import { enableTouchTooltip } from "@/models/charts/echarts-touch";

const DOWS = [1, 2, 3, 4, 5, 6, 7]; // toDayOfWeek(mode 0): 1 = пн … 7 = вс
const HOURS = Array.from({ length: 24 }, (_, h) => h);

export default {
  name: "Cm-term-rhythm",

  props: {
    // сервер отдаёт ПЛОТНЫЕ массивы: 7 дней и 24 часа, с avg_relative_er:null там, где постов не было
    byWeekday: { type: Array, default: () => [] },
    byHour: { type: Array, default: () => [] },
    tz: { type: String, default: "" },
  },

  data() {
    return {
      mode: "activity", // activity | frequency
    };
  },

  computed: {
    /** Слово могло не встретиться ни разу — пустые графики не рисуем. */
    hasData() {
      return this.byHour.some((b) => (b.posts || 0) > 0);
    },

    isFrequency() {
      return this.mode === "frequency";
    },

    dowTitle() {
      return this.$t(this.isFrequency ? "CM_DRILL_RHYTHM_FREQ_DOW" : "CM_DRILL_RHYTHM_DOW");
    },
    hourTitle() {
      return this.$t(this.isFrequency ? "CM_DRILL_RHYTHM_FREQ_HOUR" : "CM_DRILL_RHYTHM_HOUR");
    },

    /**
     * Локаль moment уже синхронизирована с i18n (i18nSetup.js) — второй системы
     * локализации не заводим. moment.weekdaysShort() ВСЕГДА начинается с воскресенья
     * (индекс 0), поэтому индексируем как dow % 7: 1(пн)→1 … 6(сб)→6, 7(вс)→0.
     */
    dowLabels() {
      const names = moment.weekdaysShort();
      return DOWS.map((d) => names[d % 7]);
    },
    hourLabels() {
      return HOURS.map((h) => String(h));
    },

    dowCells() {
      return this.withShares(this.pick(this.byWeekday, "dow", DOWS));
    },
    hourCells() {
      return this.withShares(this.pick(this.byHour, "hour", HOURS));
    },
  },

  watch: {
    // страховка: панель пересоздаётся на смену терма (loading прячет блок data),
    // так что обычно достаточно mounted, но на всякий случай реагируем и на props
    byWeekday() {
      this.$nextTick(this.renderCharts);
    },
    byHour() {
      this.$nextTick(this.renderCharts);
    },
  },

  methods: {
    setMode(mode) {
      if (this.mode === mode) return;
      this.mode = mode;
      this.$nextTick(this.renderCharts);
    },

    /** Раскладываем по ключу — от порядка в массиве не зависим. */
    pick(rows, key, buckets) {
      const byKey = new Map((rows || []).map((r) => [Number(r[key]), r]));
      return buckets.map((b) => byKey.get(b) || { share_pct: null, posts: 0 });
    },

    /**
     * Доля активности (share_pct) уже посчитана на сервере: доля средней корзины
     * от суммы средних по маргинали, сумма = 100 (см. /term → rhythm). Здесь только
     * переносим её в поле share — клиент больше сам не нормирует. Пустые корзины
     * (постов не было) остаются null.
     */
    withShares(cells) {
      return cells.map((c) => ({
        posts: c.posts,
        share: c.share_pct != null ? c.share_pct : null,
      }));
    },

    /** дни — violet, часы — green (cm-tone из Cm-top-table; не синий). */
    chartColor(isHour) {
      return isHour ? this.$colors.green : this.$colors.viol;
    },

    colorOf(value, avg, base, isPeak) {
      if (isPeak) return echarts.color.lift(base, -0.25);
      return value != null && value >= avg ? base : echarts.color.lift(base, 0.45);
    },

    /**
     * Тултип как в ecolumns-one / Activity: прозрачная оболочка +
     * .tooltip / .tooltip-list / .soc-sea и data-value на li.
     */
    tooltipFormatter(cells, labels, isHour, params) {
      if (!params.length) return "";
      const i = params[0].dataIndex;
      const cell = cells[i];
      if (!cell || !cell.posts) return ""; // пустая корзина — тултип не нужен

      const pad = (h) => String(h).padStart(2, "0");
      const head = isHour ? `${pad(i)}:00 – ${pad(i)}:59` : labels[i];
      const share = cell.share != null ? cell.share.toFixed(1) : "0";
      const color = this.chartColor(isHour);

      return `<div class="tooltip">
        <span class="date">${head}</span>
        <ul class="tooltip-list">
          <li data-value="${share}">
            <div class="soc-sea" style="color:${color}">${this.$t("CM_DRILL_RHYTHM_SHARE")}</div>${share}%
          </li>
          <li data-value="${cell.posts}">
            <div class="soc-sea">${this.$t("CM_COL_POSTS")}</div>${cell.posts}
          </li>
        </ul>
      </div>`;
    },

    chartOption(cells, labels, isHour) {
      const base = this.chartColor(isHour);
      const freq = this.isFrequency;

      // activity: доли %; frequency: число постов. null = нет постов → пропуск столбца
      const values = cells.map((c) => {
        if (!c.posts) return null;
        return freq ? c.posts : c.share;
      });

      const filled = values.filter((v) => v != null);
      const avg =
        filled.length > 0
          ? filled.reduce((s, v) => s + v, 0) / (freq ? cells.length : filled.length)
          : freq
            ? 0
            : 100 / cells.length;

      // для activity avgShare = равномерная доля 100/n; для frequency — среднее постов на корзину
      const baseline = freq ? avg : 100 / cells.length;
      const dataMax = filled.reduce((m, v) => (v > m ? v : m), 0);
      const peak = dataMax > 0 ? dataMax : null;
      // activity: доли приходят как XX.X — max оси тоже в этом формате (не 28.574…)
      const yMax = freq
        ? Math.max(Math.ceil(baseline * 2), Math.ceil(dataMax * 1.15), 1)
        : Math.ceil(Math.max(baseline * 2, Math.ceil(dataMax / 5) * 5, 5) * 10) / 10;

      const seriesName = freq
        ? this.$t("CM_COL_POSTS")
        : this.$t("CM_DRILL_RHYTHM_SHARE");

      // те же опции, что ecolumns-one: comOpt + barMaxWidth / border / shadow pointer
      return merge({}, comOpt, {
        grid: { top: 14, right: 4, bottom: 0, left: 0, containLabel: true },
        tooltip: {
          trigger: "axis",
          // на тач-устройствах нет hover — без click тултип не открывается / мигает и пропадает
          triggerOn: "mousemove|click",
          confine: true,
          axisPointer: { type: "shadow" },
          formatter: (p) => this.tooltipFormatter(cells, labels, isHour, p),
        },
        xAxis: {
          type: "category",
          data: labels,
          // панель ~520px → на график ~460px, 24 подписи по ~19px не читаются:
          // для часов оставляем каждую третью (0,3,6,…,21), для дней — все семь
          axisLabel: isHour ? { interval: (i) => i % 3 === 0 } : { interval: 0 },
        },
        yAxis: {
          type: "value",
          min: 0,
          max: yMax,
          scale: false,
          axisLabel: {
            formatter: freq
              ? "{value}"
              : (value) => `${(Math.round(Number(value) * 10) / 10).toFixed(1)}%`,
          },
        },
        series: [
          {
            name: seriesName,
            type: "bar",
            // как в ecolumns-one
            barMaxWidth: 12,
            // null (постов не было) echarts рисует ПРОПУСКОМ, а не нулём — так и надо
            data: values,
            itemStyle: {
              borderRadius: 3,
              borderWidth: 0.5,
              borderColor: "#fff",
              color: (p) => this.colorOf(p.value, baseline, base, peak != null && p.value === peak),
            },
            label: { show: false },
            emphasis: { focus: "series" },
            markLine: {
              silent: true,
              symbol: "none",
              data: [{ yAxis: baseline }],
              lineStyle: { color: "#B8BFCA", type: "dashed", width: 1 },
              label: {
                show: true,
                position: "insideEndTop",
                formatter: freq
                  ? `${Math.round(baseline * 10) / 10}`
                  : `${(Math.round(baseline * 10) / 10).toFixed(1)}%`,
                fontSize: 9,
                color: "#8a8a8a",
              },
            },
          },
        ],
      });
    },

    renderCharts() {
      if (!this.hasData) return;
      [
        ["dow", this.dowCells, this.dowLabels, false],
        ["hour", this.hourCells, this.hourLabels, true],
      ].forEach(([key, cells, labels, isHour]) => {
        const el = this.$refs[key];
        if (!el) return;
        // iOS/Android: быстрее распознаём tap, не ждём жест scroll
        el.style.touchAction = "manipulation";
        if (!this.chartInstances[key]) {
          this.chartInstances[key] = echarts.init(el, null, { renderer: "canvas" });
          enableTouchTooltip(this.chartInstances[key]);
        }
        this.chartInstances[key].setOption(this.chartOption(cells, labels, isHour), { notMerge: true });
        // ECharts кэширует размер контейнера с момента init (см. Cm-timelines) — перечитываем
        this.chartInstances[key].resize();
      });
    },

    disposeCharts() {
      Object.values(this.chartInstances).forEach((c) => c.dispose());
      this.chartInstances = {};
    },
    resizeCharts() {
      Object.values(this.chartInstances).forEach((c) => c.resize());
    },
  },

  created() {
    // инстансы echarts держим ВНЕ data (как requestId в Cm-drilldown): в data
    // Vue 2 обернул бы их реактивностью без пользы
    this.chartInstances = {};
    this.onResize = debounce(this.resizeCharts, 150);
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.$nextTick(this.renderCharts);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.onResize.cancel();
    this.disposeCharts();
  },
};
</script>
